import type { UserRole } from '@fig/shared';
import { BarChart3, FileClock, GitBranch, TrendingUp, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

const navItems = [
  { name: 'Opportunities', href: '/opportunities', icon: BarChart3, activePaths: ['/map'] },
  { name: 'Analytics', href: '/stats', icon: TrendingUp, roles: ['admin'] as UserRole[] },
  {
    name: 'My Reporting',
    href: '/reporting',
    icon: FileClock,
    roles: ['vp', 'admin'] as UserRole[],
  },
  { name: 'Operations', href: '/admin', icon: Wrench, roles: ['admin'] as UserRole[] },
  { name: 'Algorithm', href: '/algorithm', icon: GitBranch, pinBottom: true },
];

function SidebarNavLink({
  item,
  collapsed,
}: {
  item: (typeof navItems)[number];
  collapsed: boolean;
}) {
  const location = useLocation();

  return (
    <NavLink to={item.href}>
      {({ isActive }) => {
        const isAliasActive = item.activePaths?.some((path) => location.pathname === path);
        const active = isActive || isAliasActive;

        return (
          <SidebarMenuButton
            isActive={active}
            tooltip={collapsed ? item.name : undefined}
            className={cn(active && 'sidebar-nav-active')}
            style={active ? { backgroundColor: 'hsl(var(--sidebar-accent))' } : undefined}
          >
            <item.icon className="h-4 w-4" />
            <span>{item.name}</span>
          </SidebarMenuButton>
        );
      }}
    </NavLink>
  );
}

function NavItems({ items }: { items: typeof navItems }) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';

  return (
    <SidebarMenu>
      {items.map((item) => (
        <SidebarMenuItem key={item.name}>
          <SidebarNavLink key={item.name} item={item} collapsed={collapsed} />
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );
}

export function AppSidebar() {
  const { user } = useAuth();
  const { state } = useSidebar();
  const [showTitle, setShowTitle] = useState(state === 'expanded');
  const visibleItems = navItems.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role))
  );
  const primaryItems = visibleItems.filter((item) => !item.pinBottom);
  const bottomItems = visibleItems.filter((item) => item.pinBottom);

  useEffect(() => {
    if (state === 'collapsed') {
      setShowTitle(false);
      return;
    }

    const timer = window.setTimeout(() => setShowTitle(true), 220);
    return () => window.clearTimeout(timer);
  }, [state]);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-12 shrink-0 flex-row items-center border-b border-sidebar-border px-3 py-0">
        {showTitle && (
          <span className="whitespace-nowrap text-sm font-semibold">Lifecycle Review Engine</span>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <NavItems items={primaryItems} />
          </SidebarGroupContent>
        </SidebarGroup>
        {bottomItems.length > 0 && (
          <SidebarGroup className="mt-auto">
            <SidebarGroupContent>
              <NavItems items={bottomItems} />
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border px-3 py-2">
        <p className="text-[10px] text-sidebar-foreground/50 group-data-[collapsible=icon]:hidden">
          v1.0
        </p>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
